import React, { useState } from 'react';
import { getStrategyConfig, formatMetricValue } from '../config/strategyConfig';

/**
 * Parse a displayed confidence string (e.g. "82%", "medium — 70%") for threshold styling; NaN if unknown.
 */
function parseConfidencePct(confidence) {
  if (confidence == null) return NaN
  if (typeof confidence === 'number' && Number.isFinite(confidence)) {
    return confidence > 1 ? confidence : confidence * 100
  }
  const m = String(confidence).match(/(\d+(?:\.\d+)?)\s*%/)
  return m ? Number(m[1]) : NaN
}

/**
 * DynamicMetrics Component
 * Displays strategy-specific metrics in a grid format
 */
function DynamicMetrics({ strategyType, metrics, strategyData }) {
  const config = getStrategyConfig(strategyType) || {};
  const [showAllAssumptions, setShowAllAssumptions] = useState(false);
  
  // Get the 4 primary metrics to display
  const primaryMetrics = (config.primaryMetrics || []).slice(0, 4);
  
  // Get color class based on strategy
  const getBgColor = (index) => {
    const colors = ['bg-blue-50', 'bg-gray-50', 'bg-green-50', 'bg-gray-50'];
    return colors[index % colors.length];
  };

  const getTextColor = (index) => {
    const colors = ['text-blue-700', 'text-gray-900', 'text-green-700', 'text-gray-900'];
    return colors[index % colors.length];
  };

  // Get metric value from various sources
  const getMetricValue = (metricKey) => {
    // First try strategy-specific data
    if (strategyData && strategyData[metricKey] !== undefined) {
      return strategyData[metricKey];
    }
    // Then try general metrics
    if (metrics && metrics[metricKey] !== undefined) {
      return metrics[metricKey];
    }
    return null;
  };

  // Helper to convert string to title case
  const toTitleCase = (str) => {
    if (!str) return null;
    return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  // Extract rent estimate info from chart_config (structured) or key_assumptions (fallback)
  const getRentEstimateInfo = () => {
    // First try structured chart_config data
    const rentConfig = strategyData?.chart_config?.rent_estimate;
    if (rentConfig) {
      const c = rentConfig.confidence
      const confStr =
        c != null && Number.isFinite(Number(c))
          ? `${(Number(c) > 1 ? Number(c) : Number(c) * 100).toFixed(0)}%`
          : null
      return {
        confidence: confStr,
        method: rentConfig.method ? toTitleCase(rentConfig.method) : null,
        comparablesLine: rentConfig.comparables_used ? `Based on ${rentConfig.comparables_used} comparable rentals` : null,
        monthlyRent: rentConfig.monthly_rent
      };
    }
    
    // Fallback: parse from key_assumptions
    if (!strategyData?.key_assumptions) return null;
    
    const rentLine = strategyData.key_assumptions.find(a => 
      a.includes('Estimated monthly rent')
    );
    const methodLine = strategyData.key_assumptions.find(a => 
      a.includes('Method:')
    );
    const comparablesLine = strategyData.key_assumptions.find(a => 
      a.includes('comparable rentals')
    );
    
    if (!rentLine) return null;
    
    // Extract confidence percentage
    const confidenceMatch = rentLine.match(/confidence: (\d+%)/);
    const confidence = confidenceMatch ? confidenceMatch[1] : null;
    
    // Extract method
    const method = methodLine ? methodLine.replace('Method: ', '') : null;
    
    return { confidence, method, comparablesLine };
  };

  const rentInfo = getRentEstimateInfo();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h4 className="font-bold text-gray-900 mb-4">
        Key Metrics for {config.displayName}
      </h4>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {primaryMetrics.map((metricKey, index) => {
          const value = getMetricValue(metricKey);
          const label = config.metricLabels[metricKey] || metricKey;
          const formattedValue = formatMetricValue(strategyType, metricKey, value);

          return (
            <div 
              key={metricKey} 
              className={`text-center p-2 sm:p-3 ${getBgColor(index)} rounded-lg overflow-hidden`}
            >
              <div className={`text-lg sm:text-2xl font-bold ${getTextColor(index)} truncate`}>
                {formattedValue}
              </div>
              <div className="text-xs text-gray-600 mt-1 truncate" title={label}>
                {label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Rent Estimate Confidence Banner */}
      {rentInfo && (
        <div className={`mt-4 p-3 rounded-lg ${
          (() => {
            const p = parseConfidencePct(rentInfo.confidence)
            if (!Number.isFinite(p)) return 'bg-orange-50 border border-orange-200'
            if (p >= 80) return 'bg-green-50 border border-green-200'
            if (p >= 60) return 'bg-yellow-50 border border-yellow-200'
            return 'bg-orange-50 border border-orange-200'
          })()
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {rentInfo.monthlyRent && (
                <span className="text-base sm:text-lg font-bold text-gray-900">
                  £{Number(rentInfo.monthlyRent).toLocaleString()}/mo
                </span>
              )}
              <span className="text-xs sm:text-sm font-semibold text-gray-700">
                Confidence: {rentInfo.confidence}
              </span>
              {rentInfo.method && (
                <span className="text-xs text-gray-600 hidden sm:inline">
                  (Method: {rentInfo.method.replace(/_/g, ' ')})
                </span>
              )}
            </div>
            {rentInfo.comparablesLine && (
              <span className="text-xs text-green-700 font-medium">
                {rentInfo.comparablesLine.replace('Based on ', '')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Additional metrics if available */}
      {strategyData && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {/* Strategy-specific additional info */}
            {strategyData.key_assumptions && strategyData.key_assumptions.length > 0 && (
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500">Key Assumptions:</span>
                  {strategyData.key_assumptions.length > 3 && (
                    <button
                      onClick={() => setShowAllAssumptions(!showAllAssumptions)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {showAllAssumptions ? 'Show Less' : `Show All (${strategyData.key_assumptions.length})`}
                    </button>
                  )}
                </div>
                <ul className="mt-1 space-y-1">
                  {(showAllAssumptions 
                    ? strategyData.key_assumptions 
                    : strategyData.key_assumptions.slice(0, 4)
                  ).map((assumption, idx) => (
                    <li 
                      key={idx} 
                      className={`text-gray-700 flex items-start gap-2 ${
                        assumption.startsWith('⚠') ? 'text-orange-700 bg-orange-50 p-1 rounded' : ''
                      }`}
                    >
                      <span className="text-gray-400">
                        {assumption.startsWith('⚠') ? '⚠' : '•'}
                      </span>
                      <span className={assumption.startsWith('  ') ? 'text-gray-500 text-xs ml-4' : ''}>
                        {assumption.replace('⚠ ', '')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DynamicMetrics;
